<template>
  <section id="program-video" class="bg-black text-white overflow-hidden">
    <div class="lg:px-48 md:px-12 px-6 py-20">

      <!-- Заголовок -->
      <p class="font-montserrat text-xs uppercase tracking-widest text-white/40 mb-4">AWR360</p>
      <h2 class="font-pt-serif text-4xl font-bold mb-10">{{ t('video.title') }}</h2>


<div class="grid md:grid-cols-2 gap-12 items-start">

        <!-- Ліво: опис -->
        <div class="flex flex-col gap-6 relative">
          <div class="flex items-center gap-4">
            <img
              src="/images/author.jpg"
              alt="Віктор Алєксєєнко"
              class="w-20 h-20 rounded-full object-cover object-top ring-2 ring-white/10 flex-shrink-0"
            >
            <div>
              <p class="font-montserrat text-xs uppercase tracking-widest text-white/40 mb-1">{{ t('author.role') }}</p>
              <h3 class="font-pt-serif text-xl font-bold text-white">{{ t('author.name') }}</h3>
              <p class="font-montserrat text-sm text-white/40">{{ t('author.title') }}</p>
            </div>
          </div>

          <blockquote class="font-pt-serif text-2xl leading-relaxed text-white/70 border-l-2 border-[#c8962a] pl-5">
            {{ t('author.quote1') }}
          </blockquote>

          <p class="font-pt-serif text-xl leading-relaxed text-white/75">
            {{ t('author.quote2') }}
          </p>

          <p class="font-pt-serif text-xl leading-relaxed text-white/75">
            {{ t('author.quote3') }}
          </p>

          <p class="font-montserrat text-lg leading-relaxed text-white/80">
            {{ t('author.desc1') }}
          </p>

          <p class="font-montserrat text-lg leading-relaxed text-white/80">
            {{ t('author.desc2') }}
          </p>

          <p class="font-montserrat text-sm text-white/50">
            {{ t('author.available') }}
          </p>
        </div><!-- /glow wrapper -->

        <!-- Право: відео -->
        <div class="flex flex-col gap-4 items-center">
          <div ref="phoneRef" class="relative w-full max-w-xs md:max-w-md mx-auto" :style="phoneStyle">

            <!-- Артистичне підсвічення ззаду -->
            <div class="absolute -inset-12 pointer-events-none">
              <div class="absolute inset-0 bg-[#c8962a]/20 blur-3xl rounded-full" />
              <div class="absolute top-1/4 left-1/4 right-1/4 bottom-1/4 bg-[#1a4a4a]/30 blur-2xl rounded-full" />
              <div class="absolute -bottom-4 left-1/3 right-1/3 h-16 bg-[#c8962a]/25 blur-2xl rounded-full" />
            </div>

            <!-- iPhone-стиль: глянцева тонка рамка -->
            <div
              class="relative rounded-[3rem] z-10"
              style="
                padding: 1px;
                background: linear-gradient(145deg,
                  #252525 0%,
                  #141414 40%,
                  #1e1e1e 70%,
                  #101010 100%
                );
                box-shadow:
                  0 30px 80px rgba(0,0,0,0.8),
                  0 0 0 0.5px rgba(255,255,255,0.15),
                  inset 0 1px 0 rgba(255,255,255,0.4);
              "
            >
              <!-- Внутрішній глянець -->
              <div
                class="rounded-[2.9rem] relative overflow-hidden"
                style="background: #0a0a0a; padding: 10px"
              >
                <!-- Глянцевий блік зверху (дзеркальне відбиття) -->
                <div
                  class="absolute top-0 left-0 right-0 h-1/2 pointer-events-none rounded-t-[2.9rem] overflow-hidden"
                  style="background: linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)"
                />

                <!-- Dynamic Island -->
                <div class="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-[#111] rounded-full"
                     style="width: 80px; height: 22px; box-shadow: 0 0 0 1px rgba(255,255,255,0.07)" />

                <!-- Відео -->
                <div class="overflow-hidden rounded-[2.3rem] relative" style="aspect-ratio: 9/16">
                  <iframe
                    v-if="videoId"
                    :src="`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&iv_load_policy=3&fs=0&color=white&playsinline=1`"
                    class="w-full h-full"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    loading="lazy"
                    title="Вступне відео"
                  />
                  <div class="absolute top-0 left-0 right-0 h-14 bg-black pointer-events-none" />
                </div>
              </div>
            </div>

            <!-- Тонкі глянцеві кнопки -->
            <div class="absolute -right-[2px] top-28 w-[2px] h-16 rounded-r"
                 style="background: linear-gradient(to bottom, #444, #2e2e2e, #444)" />
            <div class="absolute -left-[2px] top-24 w-[2px] h-8 rounded-l"
                 style="background: linear-gradient(to bottom, #444, #2e2e2e, #444)" />
            <div class="absolute -left-[2px] top-36 w-[2px] h-8 rounded-l"
                 style="background: linear-gradient(to bottom, #444, #2e2e2e, #444)" />

          </div><!-- /wrapper -->

        </div>

      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
defineProps<{ videoId?: string }>()
const { t } = useI18n()

const phoneRef = ref<HTMLElement | null>(null)
const offsetY = ref(0)

function onScroll() {
  if (!phoneRef.value) return
  const rect = phoneRef.value.getBoundingClientRect()
  const viewH = window.innerHeight
  // progress: 0 коли входить знизу, 1 коли виходить зверху
  const progress = 1 - (rect.top + rect.height) / (viewH + rect.height)
  const clamped = Math.max(0, Math.min(1, progress))
  offsetY.value = (clamped - 0.5) * 100 // ±50px
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

const phoneStyle = computed(() => ({
  transform: `translateY(${offsetY.value}px)`,
  transition: 'transform 0.15s linear',
  willChange: 'transform',
}))
</script>
